import { useEffect, useState } from 'react'

import { useAppContext } from 'contexts/app-context/hooks/useAppContext'
import { logBridgeError, makeTraceDetails } from 'common'
import { useScreenFinishLoadTac } from 'common/hooks/useScreenFinishLoadTac'
import { isEmpty } from 'lodash'
import { useNavigate } from 'react-router-dom'
import { sendSelectContent } from 'common/helpers'
import { Routes } from 'common/constants'

import { TMenuSection } from '../types/Home.types'

function getAction(to: string) {
  if (to === Routes.NOTIFICATIONS_MANAGEMENT) return 'selectNotificationsManagement'
  if (to === Routes.YOUR_PERSONAL_DATA) return 'selectYourPersonalData'
  if (to === Routes.PRIVACY_POLICY) return 'selectPrivacyPolicy'
  if (to === Routes.FREQUENT_QUESTIONS) return 'selectFrequentQuestions'
  return 'selectUnknown'
}

export function useHome() {
  const showServiceMenu = true

  const [traceDetails, setTraceDetails] = useState('')
  const [errorOrInactive, setErrorOrInactive] = useState(false)
  const [menuSections, setMenuSections] = useState<TMenuSection[]>([])

  const {
    isAppLoading,
    isAppError,
    isAppActive,
    userCountry,
    privacyCenterContent,
    userSelectedEntity,
  } = useAppContext()

  const navigate = useNavigate()

  const isError =
    (!isAppError && isEmpty(privacyCenterContent)) || (!isAppError && !userSelectedEntity)

  const isHomeLoading = isAppLoading || isError

  const content = privacyCenterContent?.components?.home?.content

  useEffect(() => {
    if ((!isHomeLoading && privacyCenterContent !== undefined) || isAppError) {
      const isInactive = !privacyCenterContent?.components?.home?.isActive || !isAppActive

      const isErrorOrInactive = isEmpty(content) || isInactive || isAppError

      setErrorOrInactive(isErrorOrInactive)

      if (isErrorOrInactive) {
        setTraceDetails(makeTraceDetails(isInactive, content))
      }
    }
  }, [isAppActive, isAppError, content, privacyCenterContent, isHomeLoading])

  const isInactiveForTac = !privacyCenterContent?.components?.home?.isActive || !isAppActive
  const isErrorOrInactiveForTac = isEmpty(content) || isInactiveForTac || isAppError

  useScreenFinishLoadTac({
    name: 'Home',
    entity: userSelectedEntity,
    action: 'rendered_page',
    screen: Routes.HOME,
    shouldSend: !isHomeLoading && !isErrorOrInactiveForTac,
    logLabel: 'send home rendered tac',
  })

  useEffect(() => {
    if (!isHomeLoading && content) {
      const { menuSection } = content

      const menu = menuSection
        .filter((menuItem) => menuItem.isActive)
        .map((menuItem) => ({
          ...menuItem,
          onClick: async () => {
            try {
              await sendSelectContent({
                flow: 'home',
                step: 'home',
                screen_name: Routes.HOME,
                element_name: getAction(menuItem.route),
                element_type: 'button',
                parent_name: 'Home',
                scenario: JSON.stringify({
                  screen: Routes.HOME,
                  product: userSelectedEntity,
                  title: menuItem.title,
                  isActive: `${menuItem.isActive}`,
                  ref_type: 'button',
                  content_action: 'touch',
                }),
              })
            } catch (error: unknown) {
              logBridgeError('send home menu tic', error)
            }

            navigate(menuItem.route)
          },
        }))

      setMenuSections(menu)
    }
  }, [isHomeLoading, content, navigate, userSelectedEntity])

  return {
    ...content,
    userCountry,
    traceDetails,
    errorOrInactive,
    isHomeLoading,
    menuSections,
    showServiceMenu,
  }
}
